import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

interface LoadRes<T> {
  list: T[];
  total?: number;
  hasMore?: boolean;
  nextPage?: string | number;
}

type LoadFn<T, C> = (params: {
  current: C;
  pageSize: number;
}) => Promise<LoadRes<T> | void | undefined> | LoadRes<T> | void | undefined;

interface IUsePaginationProps {
  autoRun?: boolean;
  current?: string | number;
  pageSize?: number;
  maxLimit?: number;
}

const DEFAULT_PAGE_SIZE = 20;

const usePagination = function <T = any, C = any>(
  loadFn: LoadFn<T, C>,
  props?: IUsePaginationProps,
) {
  const [list, setList] = useState<T[]>([]);

  const [total, setTotal] = useState<number | void>(0);
  const [hasMore, setHasMore] = useState(true);
  const [isError, setIsError] = useState(false);

  const pageSize = useMemo(
    () => props?.pageSize || DEFAULT_PAGE_SIZE,
    [props?.pageSize],
  );
  const defaultCurrent = useMemo(() => {
    if (typeof props?.current === 'string' || typeof props?.current === 'number') {
      return props.current;
    }
    return 1;
  }, [props?.current]);

  const currentPage = useRef<C>(defaultCurrent as C);

  const loading = useRef(false);
  const pendingRefreshRef = useRef(false);
  const hasInitialized = useRef(false);
  const loadFnRef = useRef(loadFn);
  const initPromiseRef = useRef<Promise<void> | null>(null);
  const requestIdRef = useRef(0);

  // 保持 loadFn 引用最新
  useEffect(() => {
    loadFnRef.current = loadFn;
  }, [loadFn]);

  /**
   * 获取列表数据主函数
   */
  const getList = useCallback(async (isRefresh: boolean) => {
    if (loading.current) {
      if (isRefresh) {
        pendingRefreshRef.current = true;
      }
      return;
    }

    loading.current = true;
    const requestId = ++requestIdRef.current;

    if (isRefresh) {
      currentPage.current = defaultCurrent as C;
    }

    try {
      const result = await loadFnRef.current({
        pageSize,
        current: currentPage.current,
      });

      if (!result) {
        loading.current = false;
        throw new Error('load res empty');
      }

      // 检查是否是最新的请求
      if (requestId !== requestIdRef.current) {
        loading.current = false;
        return;
      }

      const { list: nextList, total, hasMore: nextHasMore, nextPage } = result;

      setList((prevList) => {
        const newList = (isRefresh ? nextList : [...prevList, ...nextList]) as T[];

        currentPage.current = (
          nextPage ? nextPage : (currentPage.current as number) + 1
        ) as C;
        setTotal(total);

        // 1.限制最大条数，超过就不获取更多了
        if (
          typeof props?.maxLimit === 'number' &&
          newList.length >= props.maxLimit
        ) {
          setHasMore(false);
        } else if (typeof nextHasMore === 'boolean') {
          // 2.手动指定hasMore
          setHasMore(nextHasMore);
        } else {
          // 3.兜底，根据单页数据条数来获取，如果小于预定要获取的pageSize，默认就是结束了
          // 为什么不用Total来获取，因为total和本地数据的条数有可能是对应不上的，可能会出现一直loading的情况
          setHasMore(nextList.length >= pageSize);
        }

        return newList;
      });
    } catch (error) {
      loading.current = false;

      // 检查是否是最新的请求
      if (requestId !== requestIdRef.current) {
        return;
      }

      setList((prevList) => {
        if (prevList.length === 0) {
          setIsError(true);
        }
        return prevList;
      });

      throw error;
    }

    loading.current = false;

    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false;
      void getList(true);
    }
  }, [pageSize, props?.current, props?.maxLimit]);

  /**
   * 上拉加载更多
   */
  const loadMore = useCallback(() => getList(false), [getList]);

  /**
   * 下拉刷新
   */
  const refresh = useCallback(() => {
    // 重置请求ID，确保旧请求不会更新状态
    requestIdRef.current = 0;
    return getList(true);
  }, [getList]);

  const clearList = useCallback(() => {
    currentPage.current = defaultCurrent as C;
    setList([]);
    setTotal(0);
    setHasMore(true);
    hasInitialized.current = false;
    // 重置请求ID
    requestIdRef.current = 0;
  }, [defaultCurrent]);

  // 是否是初次loading
  const isFirstLoad = useMemo(
    () => !isError && !list?.length && hasMore,
    [list, hasMore, isError],
  );

  const isEmpty = useMemo(() => !list?.length && !hasMore, [list, hasMore]);

  // 初始化加载 - 使用独立的 useEffect，不依赖 loadMore
  useEffect(() => {
    // 防止重复初始化加载：
    // 1. 已经初始化过
    // 2. 正在加载中（getList 内部会设置）
    // 3. 已经有待处理的初始化 Promise
    // 4. 已经有数据了（避免 React Strict Mode 下的重复请求）
    if (hasInitialized.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[usePagination] 跳过初始化：已经初始化过');
      }
      return;
    }

    if (loading.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[usePagination] 跳过初始化：正在加载中');
      }
      return;
    }

    if (initPromiseRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[usePagination] 跳过初始化：已有待处理的 Promise');
      }
      return;
    }

    // 检查是否已有数据
    if (list.length > 0) {
      hasInitialized.current = true;
      if (process.env.NODE_ENV === 'development') {
        console.log('[usePagination] 跳过初始化：已有数据', list.length);
      }
      return;
    }

    if (props?.autoRun || typeof props?.autoRun === 'undefined') {
      if (process.env.NODE_ENV === 'development') {
        console.log('[usePagination] 开始初始化加载');
      }

      // 先设置 hasInitialized 标志，防止重复执行
      // 必须在调用 getList 之前设置，确保同步检查有效
      hasInitialized.current = true;

      // 创建并保存 Promise，防止重复请求
      const initPromise = getList(false)
        .then(() => {
          // 请求成功后清除 Promise 引用
          initPromiseRef.current = null;
          if (process.env.NODE_ENV === 'development') {
            console.log('[usePagination] 初始化加载成功');
          }
        })
        .catch((error) => {
          // 如果初始化失败，重置标志以便重试
          hasInitialized.current = false;
          initPromiseRef.current = null;
          if (process.env.NODE_ENV === 'development') {
            console.error('[usePagination] 初始化加载失败', error);
          }
        });

      initPromiseRef.current = initPromise;
    }
    // 注意：这里不包含 list 和 getList 在依赖数组中，因为我们只关心 autoRun 的变化
    // 使用 hasInitialized、loading 和 initPromiseRef 来防止重复执行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.autoRun]);

  const mutate = (newList: T[]) => {
    setList(newList);
  };

  return {
    list,
    total,
    hasMore,
    isFirstLoad,
    isEmpty,
    isError,
    loading: loading.current,
    loadMore,
    refresh,
    clearList,
    mutate,
  };
};

export default usePagination;